import React, { memo, forwardRef, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';

// Styled container for pagination dots
const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  margin: `${theme.spacing(1)} 0`,
  minHeight: '30px',
}));

// Styled motion-based Dot component
const Dot = styled(motion(Box), {
  shouldForwardProp: (prop) => prop !== 'active', // Prevent 'active' prop from being passed to the DOM
})(({ theme, active }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: active ? theme.palette.primary.main : theme.palette.grey[600],
  margin: '0 5px',
  cursor: 'pointer',
  transition: 'background-color 0.3s',
}));

// Main PaginationDots component with forwardRef
const PaginationDots = forwardRef(({ totalPages=10, currentPage=0, onDotClick=(pageIndex)=>{ console.log(pageIndex,'pageIndex')} }, ref) => {
  if (totalPages <= 0) return null; // Handle invalid totalPages

  const safeCurrentPage = useMemo(() => Math.min(Math.max(currentPage, 0), totalPages - 1), [currentPage, totalPages]);

  return (
    <PaginationContainer component="div" ref={ref}>
      {Array.from({ length: totalPages }, (_, pageIndex) => (
        <Dot
          key={pageIndex}
          active={safeCurrentPage === pageIndex}
          onClick={(e) => { 
            e.stopPropagation();
            if (safeCurrentPage !== pageIndex) onDotClick(pageIndex);
          }}
          aria-label={`Go to slide ${pageIndex + 1}`}
          initial={{ scale: 1, opacity: 0.6 }}
          animate={{
            scale: safeCurrentPage === pageIndex ? 1.5 : 1,
            opacity: safeCurrentPage === pageIndex ? 1 : 0.6,
          }}
          transition={{ type: 'spring', stiffness: 300 }}
          whileHover={{ scale: 1.2 }}
        />
      ))}
    </PaginationContainer>
  );
});

// Memoized for optimization
export default memo(PaginationDots);
